import { Suspense, useEffect, useState, type ComponentProps } from "react";
import { Loader2 } from "lucide-react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const loadImpl = () => import("./AvailabilityModal");
const AvailabilityModalImpl = lazyWithRetry(loadImpl);
const QueryProvider = lazyWithRetry(() => import("@/components/QueryProvider"));

/**
 * Kicks off the modal chunk download before it's needed. Call from CTA
 * hover/focus handlers; also runs automatically on browser idle.
 */
export const preloadAvailabilityModal = () => {
  void loadImpl();
};

type Props = ComponentProps<typeof AvailabilityModalImpl>;

// Instant visual feedback while the modal chunk downloads on a cold cache.
const ModalLoadingFallback = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
    <div className="flex flex-col items-center gap-3 text-cream">
      <Loader2 className="h-8 w-8 animate-spin text-gold" />
      <p className="text-sm tracking-wide">Loading booking calendar…</p>
    </div>
  </div>
);

/**
 * Defers the (large) booking modal bundle until it's likely needed.
 * The chunk is prefetched on idle/hover so it stays out of the initial
 * page JS without making the first click feel slow.
 */
const LazyAvailabilityModal = (props: Props) => {
  const [mounted, setMounted] = useState(props.open);

  useEffect(() => {
    if (props.open) setMounted(true);
  }, [props.open]);

  // Warm the chunk once the browser is idle so the first click is instant.
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(preloadAvailabilityModal, { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(preloadAvailabilityModal, 2000);
    return () => window.clearTimeout(id);
  }, []);

  if (!mounted) return null;

  return (
    <Suspense fallback={props.open ? <ModalLoadingFallback /> : null}>
      <QueryProvider>
        <AvailabilityModalImpl {...props} />
      </QueryProvider>
    </Suspense>
  );
};

export default LazyAvailabilityModal;
